import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const EMPTY_PASSWORDS = { currentPassword: '', newPassword: '', confirmPassword: '' };

const readResponse = async response => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || 'Something went wrong');
  return data;
};

const Detail = ({ label, value }) => (
  <div className="profileDetail">
    <dt>{label}</dt>
    <dd>{value || 'Not provided'}</dd>
  </div>
);

export const Profile = () => {
  const dispatch = useDispatch();
  const [profile, setProfile] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState('');
  const [editing, setEditing] = useState(false);
  const [profileStatus, setProfileStatus] = useState({ type: '', message: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwords, setPasswords] = useState(EMPTY_PASSWORDS);
  const [passwordStatus, setPasswordStatus] = useState({ type: '', message: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/apiprofile')
      .then(readResponse)
      .then(data => { if (!cancelled) { setProfile(data); setDetails(data); } })
      .catch(error => { if (!cancelled) setProfileError(error.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const updatePassword = event => {
    event.preventDefault();
    setPasswordStatus({ type: '', message: '' });
    if (passwords.newPassword.length < 12) {
      return setPasswordStatus({ type: 'error', message: 'New password must be at least 12 characters' });
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      return setPasswordStatus({ type: 'error', message: 'New passwords do not match' });
    }
    setSavingPassword(true);
    fetch('/apiprofile/password', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(passwords)
    })
      .then(readResponse)
      .then(data => {
        setPasswords(EMPTY_PASSWORDS);
        setPasswordStatus({ type: 'success', message: data.message });
      })
      .catch(error => setPasswordStatus({ type: 'error', message: error.message }))
      .finally(() => setSavingPassword(false));
  };

  const updateField = event => setPasswords(current => ({ ...current, [event.target.name]: event.target.value }));
  const updateDetail = event => setDetails(current => ({ ...current, [event.target.name]: event.target.value }));

  const saveProfile = event => {
    event.preventDefault();
    setSavingProfile(true);
    setProfileStatus({ type: '', message: '' });
    fetch('/apiprofile', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: details.username,
        email: details.email,
        firstName: details.firstName,
        lastName: details.lastName,
        title: details.title,
        phoneNumber: details.phoneNumber
      })
    })
      .then(readResponse)
      .then(data => {
        setProfile(data.profile);
        setDetails(data.profile);
        setEditing(false);
        setProfileStatus({ type: 'success', message: data.message });
        dispatch({ type: 'LOG_IN', payload: data.user });
        sessionStorage.setItem('currentUser', JSON.stringify(data.user));
      })
      .catch(error => setProfileStatus({ type: 'error', message: error.message }))
      .finally(() => setSavingProfile(false));
  };

  return (
    <section className="pageSection profilePage">
      <header className="profileHeader">
        <div><h1>My profile</h1><p>View your portal details and manage your password.</p></div>
      </header>
      {loading && <div className="profileState" role="status">Loading profile…</div>}
      {profileError && <div className="profileState profileMessageError" role="alert">{profileError}</div>}
      {profile && (
        <div className="profileGrid">
          <article className="profileCard">
            <div className="profileCardHeading">
              <div className="profileAvatar" aria-hidden="true">{(profile.firstName || profile.username || 'U').charAt(0).toUpperCase()}</div>
              <div><h2>User details</h2><p>{profile.fullName || profile.username}</p></div>
              {!editing && <button type="button" className="profileEditButton" onClick={() => { setDetails(profile); setEditing(true); setProfileStatus({ type: '', message: '' }); }}>Edit profile</button>}
            </div>
            {editing ? (
              <form className="profileDetailsForm" onSubmit={saveProfile}>
                <label htmlFor="profileUsername">Username</label>
                <input id="profileUsername" name="username" value={details.username} onChange={updateDetail} minLength="3" maxLength="50" required />
                <label htmlFor="profileEmail">Email</label>
                <input id="profileEmail" name="email" type="email" value={details.email} onChange={updateDetail} maxLength="300" required />
                <div className="profileNameFields">
                  <div><label htmlFor="profileFirstName">First name</label><input id="profileFirstName" name="firstName" value={details.firstName} onChange={updateDetail} maxLength="300" required /></div>
                  <div><label htmlFor="profileLastName">Last name</label><input id="profileLastName" name="lastName" value={details.lastName} onChange={updateDetail} maxLength="300" required /></div>
                </div>
                <label htmlFor="profileTitle">Title</label>
                <input id="profileTitle" name="title" value={details.title} onChange={updateDetail} maxLength="150" disabled={!profile.quickbaseContactLinked} />
                <label htmlFor="profilePhone">Phone number</label>
                <input id="profilePhone" name="phoneNumber" type="tel" value={details.phoneNumber} onChange={updateDetail} maxLength="50" disabled={!profile.quickbaseContactLinked} />
                {!profile.quickbaseContactLinked && <p className="profileFieldNote">Link a Quickbase Client Contact to edit title and phone.</p>}
                {profileStatus.message && <div className={profileStatus.type === 'success' ? 'profileMessageSuccess' : 'profileMessageError'} role={profileStatus.type === 'success' ? 'status' : 'alert'}>{profileStatus.message}</div>}
                <div className="profileFormActions">
                  <button className="profileSubmitButton" type="submit" disabled={savingProfile}>{savingProfile ? 'Saving…' : 'Save changes'}</button>
                  <button className="profileCancelButton" type="button" disabled={savingProfile} onClick={() => { setDetails(profile); setEditing(false); setProfileStatus({ type: '', message: '' }); }}>Cancel</button>
                </div>
              </form>
            ) : (
              <>
                <dl className="profileDetails">
                  <Detail label="Username" value={profile.username} />
                  <Detail label="Email" value={profile.email} />
                  <Detail label="Name" value={profile.fullName} />
                  <Detail label="Title" value={profile.title} />
                  <Detail label="Phone number" value={profile.phoneNumber} />
                </dl>
                {profileStatus.message && <div className={profileStatus.type === 'success' ? 'profileMessageSuccess' : 'profileMessageError'} role={profileStatus.type === 'success' ? 'status' : 'alert'}>{profileStatus.message}</div>}
              </>
            )}
          </article>
          <article className="profileCard">
            <h2>Update password</h2>
            <p className="profileCardIntro">Use at least 12 characters for your new password.</p>
            <form className="profilePasswordForm" onSubmit={updatePassword}>
              <label htmlFor="currentPassword">Current password</label>
              <input id="currentPassword" name="currentPassword" type="password" autoComplete="current-password" value={passwords.currentPassword} onChange={updateField} required />
              <label htmlFor="newPassword">New password</label>
              <input id="newPassword" name="newPassword" type="password" autoComplete="new-password" minLength="12" value={passwords.newPassword} onChange={updateField} required />
              <label htmlFor="confirmPassword">Confirm new password</label>
              <input id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" minLength="12" value={passwords.confirmPassword} onChange={updateField} required />
              {passwordStatus.message && <div className={passwordStatus.type === 'success' ? 'profileMessageSuccess' : 'profileMessageError'} role={passwordStatus.type === 'success' ? 'status' : 'alert'}>{passwordStatus.message}</div>}
              <button className="profileSubmitButton" type="submit" disabled={savingPassword}>{savingPassword ? 'Updating…' : 'Update password'}</button>
            </form>
          </article>
        </div>
      )}
    </section>
  );
};
